<script setup>
import {ref, computed, watch, onMounted, nextTick, markRaw} from 'vue'
import '@/js/codemirror.js'
import {useUserStore} from '@/stores/user'
import {useThemesStore} from '@/stores/themes'

const props = defineProps({
    data: Object,
})

// Pinia Stores
const userStore = useUserStore()
const themesStore = useThemesStore()

// State
const show = ref(false)
const value = ref(props.data.code)
const mode = ref(props.data.mode)
const codeMirrorInstance = ref(undefined)
const codeMirrorRef = ref(null)

// Computed Properties
const selections = computed(() => {
    return themesStore.findModalSelections
})

const theme = computed(() => {
    return userStore.pref('codeMirror_theme')
})

const lineNumbers = computed(() => {
    return userStore.pref('codeMirror_lineNumbers')
})

const lineWrapping = computed(() => {
    return userStore.pref('codeMirror_lineWrapping')
})

const twigMode = computed(() => {
    return userStore.pref('codeMirror_twigMode')
})

// Watchers
watch(theme, (newValue) => {
    codeMirrorInstance.value?.setOption('theme', newValue)
})

watch(lineNumbers, (newValue) => {
    codeMirrorInstance.value?.setOption('lineNumbers', newValue)
})

watch(lineWrapping, (newValue) => {
    codeMirrorInstance.value?.setOption('lineWrapping', newValue)
})

watch(twigMode, (newValue) => {
    // Treat 'twig' and 'text/x-twig' as the same starting point
    if (mode.value === 'text/html' || mode.value === 'text/x-twig' || mode.value === 'twig') {
        mode.value = newValue ? 'text/x-twig' : 'text/html'
    }

    if (codeMirrorInstance.value) {
        codeMirrorInstance.value.setOption('mode', mode.value)

        // CRITICAL: Force CodeMirror to repaint the syntax highlighting tokens
        // sometimes changing options on-the-fly doesn't trigger a visual re-tokenization
        codeMirrorInstance.value.setValue(codeMirrorInstance.value.getValue())
    }
})

// Internal Methods
const setSelections = () => {
    if (selections.value.length) {
        // Highlight the first selection only to prevent multi-instance replacement
        const selectionToApply = [selections.value[0]]
        codeMirrorInstance.value.setSelections(selectionToApply)
        themesStore.setFindModalSelections([])
    }
}

const setCodeMirrorOptions = () => {
    if (props.data.history) {
        codeMirrorInstance.value.setHistory(props.data.history)
    } else {
        props.data.originalCode = codeMirrorInstance.value.getValue()
    }

    if (props.data.scrollInfo) {
        codeMirrorInstance.value.scrollTo(props.data.scrollInfo.left, props.data.scrollInfo.top)
    }

    // Replace tabs with spaces
    codeMirrorInstance.value.setOption('extraKeys', {
        Tab: (cm) => {
            const spaces = Array(cm.getOption('indentUnit') + 1).join(' ')
            cm.replaceSelection(spaces)
        }
    })
}

const setCodeMirrorEvents = () => {
    codeMirrorInstance.value.on('scroll', () => {
        props.data.scrollInfo = codeMirrorInstance.value.getScrollInfo()
    })

    codeMirrorInstance.value.on('change', () => {
        props.data.history = codeMirrorInstance.value.getHistory()
        props.data.code = codeMirrorInstance.value.getValue()
        props.data.isModified = JSON.stringify(props.data.originalCode) !== JSON.stringify(props.data.code)
    })

    codeMirrorInstance.value.on('focus', () => {
        props.data.focused = true
        themesStore.setCodeMirror(codeMirrorInstance.value)
    })

    codeMirrorInstance.value.on('blur', () => {
        props.data.focused = false
    })
}

// Lifecycle Hooks
onMounted(() => {
    let codeMirrorOptions = {
        mode: mode.value,
        lineNumbers: true,
        lineWrapping: false,
        readOnly: props.data.readOnly,
        indentUnit: 4,
        tabMode: 'spaces',
        enterMode: 'indent',
        electricChars: false,
        autofocus: true,
        styleActiveLine: {nonEmpty: true},
        autoCloseTags: true
    }

    if (theme.value) {
        codeMirrorOptions['theme'] = theme.value
    }

    if (lineNumbers.value !== undefined) {
        codeMirrorOptions['lineNumbers'] = lineNumbers.value
    }

    if (lineWrapping.value !== undefined) {
        codeMirrorOptions['lineWrapping'] = lineWrapping.value
    }

    if (twigMode.value !== undefined) {
        if (mode.value === 'text/html' || mode.value === 'text/x-twig') {
            mode.value = twigMode.value ? 'text/x-twig' : 'text/html'
        }
        codeMirrorOptions['mode'] = mode.value
    }

    if (mode.value === 'javascript' || mode.value === 'css') {
        Object.assign(codeMirrorOptions, {
            gutters: ['CodeMirror-lint-markers'],
            lint: true
        })
    }

    show.value = true

    nextTick(() => {
        // Note: Assumes CodeMirror is globally available via script tag as per original
        const cmInstance = CodeMirror.fromTextArea(codeMirrorRef.value, codeMirrorOptions)
        codeMirrorInstance.value = markRaw(cmInstance)
        codeMirrorInstance.value.setSize('100%', '100%')

        setSelections()
        setCodeMirrorOptions()
        setCodeMirrorEvents()
    })
})
</script>

<template>
    <textarea v-if="show" ref="codeMirrorRef">{{ value }}</textarea>
</template>